/**
 * This module implements our uploader. Currently we use S3, but if we ever need
 * to swap, then in theory we just need to provide the same Vuex interface.
 * 
 * For attachment to DB models and process choreography, see attachments.js
 * For files collection CRUD, see files.js
 * For uploading (to s3), see upload.js
 * 
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */

import api from '@/api'
import s3, { bucketName } from '@/api/aws'
import { Upload } from '@aws-sdk/lib-storage'

import {
	VUEX_UPLOAD_REQUEST,
	VUEX_UPLOAD_ABORT,
	VUEX_UPLOAD_REQUEST_SUCCESS,
	VUEX_UPLOAD_REQUEST_FAILURE,
	VUEX_UPLOAD_SET_FILE_TO_UPLOAD,

	VUEX_UPLOAD_S3_PART_SIZE,
	VUEX_UPLOAD_S3_QUEUE_SIZE,
	VUEX_UPLOAD_S3_ACL
    } from '@/store/constants/upload'

import {
	VUEX_FILE_SHOW_REQUEST
	} from '@/store/constants/files'

import {
	VUEX_ATTACHMENT_QUEUE_MANAGER_UPLOAD_PROGRESS,
	VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_S3_KEY,
	VUEX_ATTACHMENT_QUEUE_MANAGER_HANDLE_UPLOAD_RESULT
	} from '@/store/constants/attachment_queue_manager'

/**
 * Module state
 * @const
 */
const state = {
	
	// Use these during upload
	fileToUpload: null,
	fileUploadDbRecord: null,
	fileUploadStatus: null,

	fileUploadProgressData: {},

	// Record an error for later
	lastError: null,
}

/**
 * Module getters
 * @const
 */
const getters = {}

/**
 * Module action definitions
 * @const
 */
const actions = {

	/**
	 * Upload a file to S3
	 */
	[VUEX_UPLOAD_REQUEST]: async({ state, commit, dispatch }, payload) => {
	    // Notify that we are in here
		await commit(VUEX_UPLOAD_REQUEST, { 
			hashId: payload.hashId, 
			status: SILA_FILE_UPLOAD_STATUS_STARTED 
		})

		// Prep some variables for the rest of this excitement
		let uploadPath = SILA_FILE_UPLOAD_PATH ? SILA_FILE_UPLOAD_PATH : 'lost_and_found'

		// Build our file key for S3
		let destinationFileKey = `${uploadPath}/${payload.id}/${payload.filename}`

		// Store the key on teh file object because it is the only reliable way to track the
		// file through and after the S3 process
		dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_S3_KEY, { 
			hashId: payload.hashId, 
			key:    destinationFileKey
		})

		// Define a handler for the async response
		const handleS3Response = function(error, data) {
		    if (error) {
				// ON S3 UPLOAD - FAILURE
				dispatch(VUEX_UPLOAD_REQUEST_FAILURE, {
					hashId: payload.hashId,
					error: error
				})
			} else {
				// ON S3 UPLOAD - SUCCESS
				dispatch(VUEX_UPLOAD_REQUEST_SUCCESS, {
					hashId: payload.hashId,
					data: data
				})
			}
		}

		// v3 lib-storage emits `Key` (capitalized) on the progress event; the
		// progress mutation matches files by lowercase `key`, so map it back.
		const handleS3UploadProgress = function(evt) {
			commit(VUEX_ATTACHMENT_QUEUE_MANAGER_UPLOAD_PROGRESS, {
				key:    evt.Key,
				loaded: evt.loaded,
				total:  evt.total,
			})
		}

		var params = {
			Bucket: bucketName,
			Key:    destinationFileKey,
			Body:   payload.file,
			ACL:    VUEX_UPLOAD_S3_ACL,
		}

		/*** UPLOAD TO S3 ***/
		const upload = new Upload({
			client: s3,
			params: params,
			partSize:  VUEX_UPLOAD_S3_PART_SIZE,
			queueSize: VUEX_UPLOAD_S3_QUEUE_SIZE,
		})

		upload.on('httpUploadProgress', handleS3UploadProgress)

		upload.done()
			.then(data => handleS3Response(null, data))
			.catch(error => handleS3Response(error))
	},

	/**
	 * Amend the DB record upon success
	 */
	[VUEX_UPLOAD_REQUEST_SUCCESS]: async({ commit, dispatch, state }, payload) => {
		await commit(VUEX_UPLOAD_REQUEST_SUCCESS, {
			status: SILA_FILE_UPLOAD_STATUS_SUCCESS,
			data: payload.data
		})

		dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_HANDLE_UPLOAD_RESULT, {
			hashId: payload.hashId,
			uri: payload.data.Location,
			status: SILA_FILE_UPLOAD_STATUS_SUCCESS
		})
	},

	/**
	 * Amend the DB record upon failure
	 */
	[VUEX_UPLOAD_REQUEST_FAILURE]: async({ commit, dispatch, state }, payload) => {
		commit(VUEX_UPLOAD_REQUEST_FAILURE, {
			status: SILA_FILE_UPLOAD_STATUS_FAILURE,
			error: payload.error
		})


		dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_HANDLE_UPLOAD_RESULT, {
			hashId: payload.hashId,
			status: SILA_FILE_UPLOAD_STATUS_FAILURE
		})
	},

}

/**
 * Module mutation definitions
 * @const
 */
const mutations = {
	/**
	 * The upload mutations that track progress to S3
	 */
	[VUEX_UPLOAD_SET_FILE_TO_UPLOAD]: (state, file) => {
		// state.fileToUpload = file
	},

	[VUEX_UPLOAD_REQUEST]: (state, payload) => {
		// state.fileUploadStatus = payload.status
	},

	[VUEX_UPLOAD_REQUEST_SUCCESS]: (state, payload) => {
		// state.fileUploadStatus = payload.status
		// state.fileToUpload = null
	},

	[VUEX_UPLOAD_REQUEST_FAILURE]: (state, payload) => {
		// state.fileUploadStatus = payload.status
		// state.fileToUpload = null
		console.error('Trouble with the upload: ', payload.error.message);
	},
}

/**
 * The Files module of our Vuex store.
 * @module store/modules/files
 */
export default {
  state,
  getters,
  actions,
  mutations,
}
